import { type ReactElement, useEffect, useRef, useState } from 'react';
import { toast } from 'react-hot-toast';
import { useTranslation } from 'react-i18next';
import type { ParseKeys } from 'i18next';
import type { AIAssistantClientSkill, AIAssistantClientSkillInput, ViewModelId } from 'balus-common';
import {
  deleteViewModelAIAssistantSkill,
  subscribeViewModelAIAssistantSkills,
  type ViewModelAIAssistantSkillMutationErrorCode,
} from '@view-model/application/ai/ViewModelAIAssistantSkillSource';
import {
  commitViewModelAIAssistantSkillChange,
  prepareViewModelAIAssistantSkillCreate,
  prepareViewModelAIAssistantSkillUpdate,
} from '@view-model/application/ai/ViewModelAIAssistantSkillChange';
import { AIAssistantSkillSettings, type SkillDraft, type SkillMutationRequest } from './AIAssistantSkillSettings';

const SKILL_MUTATION_ERROR_MESSAGE_KEYS = {
  invalidSkill: 'viewModel.aiSettings.skillValidationError',
  skillLimit: 'viewModel.aiSettings.skillLimitError',
  skillNotFound: 'viewModel.aiSettings.skillNotFoundError',
  duplicateName: 'viewModel.aiSettings.skillDuplicateNameError',
  conflict: 'viewModel.aiSettings.skillConflictError',
} as const satisfies Record<ViewModelAIAssistantSkillMutationErrorCode, ParseKeys>;

interface State {
  skills: AIAssistantClientSkill[];
  draft: SkillDraft | null;
}

function buildSkillInput(skill: AIAssistantClientSkill): AIAssistantClientSkillInput {
  return {
    enabled: skill.enabled,
    name: skill.name,
    description: skill.description,
    triggers: skill.triggers,
    whenToUse: skill.whenToUse,
    argumentHint: skill.argumentHint,
    prompt: skill.prompt,
  };
}

export function AIAssistantSkillsPage({ viewModelId }: { viewModelId: ViewModelId }): ReactElement {
  const { t } = useTranslation();
  const skillLoadFailedMessage = t('common.message.loadFailed');
  const isSavingSkillsRef = useRef(false);
  const [state, setState] = useState<State>({
    skills: [],
    draft: null,
  });
  const [isSavingSkills, setIsSavingSkills] = useState(false);

  useEffect(() => {
    return subscribeViewModelAIAssistantSkills(
      viewModelId,
      (skills) => {
        setState((prev) => ({ ...prev, skills }));
      },
      (error) => {
        console.error('Failed to load AI assistant skills', error);
        toast.error(skillLoadFailedMessage);
      }
    );
  }, [skillLoadFailedMessage, viewModelId]);

  const showMutationError = (code: ViewModelAIAssistantSkillMutationErrorCode): void => {
    toast.error(t(SKILL_MUTATION_ERROR_MESSAGE_KEYS[code]));
  };

  const mutateSkill = async (mutation: SkillMutationRequest): Promise<boolean> => {
    if (isSavingSkillsRef.current) return false;

    isSavingSkillsRef.current = true;
    setIsSavingSkills(true);
    try {
      if (mutation.type === 'delete') {
        const result = await deleteViewModelAIAssistantSkill(viewModelId, mutation.expectedSkill);
        if (result.type === 'error') {
          showMutationError(result.code);
          return false;
        }

        setState((prev) => ({ ...prev, skills: result.skills }));
        return true;
      }

      const preparedResult = (() => {
        if (mutation.type === 'create') {
          return prepareViewModelAIAssistantSkillCreate(mutation.input, state.skills);
        }

        const input =
          mutation.type === 'update'
            ? mutation.input
            : { ...buildSkillInput(mutation.expectedSkill), enabled: mutation.enabled };
        return prepareViewModelAIAssistantSkillUpdate(mutation.expectedSkill, input, state.skills);
      })();

      if (preparedResult.type === 'error') {
        showMutationError(preparedResult.code);
        return false;
      }

      const result = await commitViewModelAIAssistantSkillChange(viewModelId, preparedResult.prepared);
      if (result.type === 'error') {
        showMutationError(result.code);
        return false;
      }

      setState((prev) => ({ ...prev, skills: result.skills }));
      return true;
    } catch (error) {
      toast.error(t('common.message.saveSettingsFailed'));
      console.error('Failed to save AI assistant skills', error);
      return false;
    } finally {
      isSavingSkillsRef.current = false;
      setIsSavingSkills(false);
    }
  };

  return (
    <div className="flex min-h-0 grow flex-col overflow-hidden">
      <AIAssistantSkillSettings
        skills={state.skills}
        draft={state.draft}
        isSaving={isSavingSkills}
        onSkillMutation={mutateSkill}
        onDraftChange={(draft) => {
          setState((prev) => ({ ...prev, draft }));
        }}
      />
    </div>
  );
}
